/**
 * VM Management Page
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Header from '../components/layout/Header';
import { KYCRequiredModal } from '../components/kyc';
import { useVMs } from '../hooks/useVMs';
import { VMTableRow } from '../components/vms/VMTableRow';
import { VMConnectionInfo } from '../components/vms/VMConnectionInfo';
import CreateSnapshotModal from '../components/vms/CreateSnapshotModal';
import { VM } from '../types/vm';
import { Project } from '../types';
import { useSelectedProject } from '../hooks/useSelectedProject';
import { useKYCStatus } from '../hooks/useKYCStatus';

const VMs: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawProjectParam = searchParams.get('project');
  const statusFromUrl = searchParams.get('status');
  const pageFromUrl = searchParams.get('page');

  const {
    selectedProjectId: globalProjectId,
    projects,
    setSelectedProject: setGlobalProject,
    autoSelectFirstProject,
  } = useSelectedProject();

  // Missing `project` in URL → use profile default; present (including `?project=`) → use that value
  const selectedProject =
    rawProjectParam === null ? globalProjectId : rawProjectParam;

  const [statusFilter, setStatusFilter] = useState<string>(statusFromUrl || 'all');
  const [page, setPage] = useState<number>(pageFromUrl ? parseInt(pageFromUrl, 10) : 1);

  // Auto-select first project if none selected and no explicit project in URL
  useEffect(() => {
    if (rawProjectParam === null) {
      autoSelectFirstProject();
    }
  }, [rawProjectParam, autoSelectFirstProject]);

  useEffect(() => {
    if (statusFromUrl) {
      setStatusFilter(statusFromUrl);
    }
  }, [statusFromUrl]);

  useEffect(() => {
    if (pageFromUrl) {
      const pageNum = parseInt(pageFromUrl, 10);
      if (!isNaN(pageNum) && pageNum > 0) {
        setPage(pageNum);
      }
    }
  }, [pageFromUrl]);
  
  const projectId =
    selectedProject === '' ? undefined : selectedProject || undefined;
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [selectedVMForSnapshot, setSelectedVMForSnapshot] = useState<string | null>(null);
  const [selectedVM, setSelectedVM] = useState<VM | null>(null);
  const [showConnectionInfo, setShowConnectionInfo] = useState(false);
  const [showKYCModal, setShowKYCModal] = useState(false);

  const { isKYCVerified } = useKYCStatus();
  
  // Keep URL in sync with filters (supports explicit "all projects" via empty project param)
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedProject === '') {
      params.set('project', '');
    } else if (selectedProject) {
      params.set('project', selectedProject);
    }
    if (statusFilter !== 'all') {
      params.set('status', statusFilter);
    }
    if (page > 1) {
      params.set('page', page.toString());
    }
    const newParamsString = params.toString();
    const currentParamsString = searchParams.toString();
    if (newParamsString !== currentParamsString) {
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, statusFilter, page, setSearchParams]);
  
  const {
    vms,
    runningCount,
    stoppedCount,
    totalCount,
    page: currentPage,
    pages,
    isLoading,
    isFetching,
    isError,
    refetch,
    startVM,
    isStarting,
    stopVM,
    isStopping,
    restartVM,
    isRestarting,
    deleteVM,
    isDeleting,
    retryVM,
    isRetrying,
    syncStatus,
    isSyncingStatus,
  } = useVMs({
    projectId,
    page,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  });

  // Auto-refresh: Poll more often when any VM is "creating" so status updates to Running quickly
  const hasCreatingVMs = vms.some((v) => v.status === 'creating');
  const pollIntervalMs = hasCreatingVMs ? 5000 : 30000; // 5s while creating, else 30s
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, pollIntervalMs);
    return () => clearInterval(interval);
  }, [refetch, pollIntervalMs]);

  const handleCreateVM = () => {
    if (!isKYCVerified) {
      setShowKYCModal(true);
      return;
    }
    const url = selectedProject ? `/vms/create?project=${selectedProject}` : '/vms/create';
    navigate(url);
  };

  const handleStartVM = (vmId: string) => {
    startVM(vmId);
  };

  const handleStopVM = (vmId: string) => {
    stopVM(vmId);
  };

  const handleRestartVM = (vmId: string) => {
    restartVM(vmId);
  };

  const handleDeleteVM = (vmId: string) => {
    deleteVM(vmId);
  };

  const handleRetryVM = (vmId: string) => {
    retryVM(vmId);
  };

  const handleSyncStatus = (vmId: string) => {
    syncStatus(vmId);
  };

  // Listen for connection info events from table rows
  useEffect(() => {
    const handleViewConnectionInfo = (event: Event) => {
      const customEvent = event as CustomEvent;
      setSelectedVM(customEvent.detail);
      setShowConnectionInfo(true);
    };

    window.addEventListener('viewConnectionInfo', handleViewConnectionInfo);
    return () => {
      window.removeEventListener('viewConnectionInfo', handleViewConnectionInfo);
    };
  }, []);

  return (
    <div>
      <Header
        title="VM Management"
        subtitle="Manage your virtual machines"
        actions={
          <button
            onClick={handleCreateVM}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            Create VM
          </button>
        }
      />

      <div className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400">Running</p>
                <p className="mt-2 text-3xl font-bold text-green-400">{runningCount}</p>
              </div>
              <div className="bg-green-500/20 p-3 rounded-xl">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400">Stopped</p>
                <p className="mt-2 text-3xl font-bold text-gray-400">{stoppedCount}</p>
              </div>
              <div className="bg-gray-700 p-3 rounded-xl">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400">Total</p>
                <p className="mt-2 text-3xl font-bold text-white">{totalCount}</p>
              </div>
              <div className="bg-primary-500/20 p-3 rounded-xl">
                <svg className="w-6 h-6 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-300 whitespace-nowrap">Project:</label>
              <select
                value={selectedProject}
                onChange={(e) => {
                  const newProject = e.target.value;
                  setPage(1);
                  const next = new URLSearchParams(searchParams);
                  if (newProject) {
                    next.set('project', newProject);
                    setGlobalProject(newProject);
                  } else {
                    next.set('project', '');
                  }
                  setSearchParams(next, { replace: true });
                }}
                className="px-3 py-2 border border-gray-600 rounded-md text-sm bg-gray-800 text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[200px] transition-colors"
                aria-label="Filter by project"
              >
                <option value="">All Projects</option>
                {projects.map((project: Project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-300 whitespace-nowrap">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  const newStatus = e.target.value;
                  setStatusFilter(newStatus);
                  setPage(1); // Reset to first page when filter changes
                }}
                className="px-3 py-2 border border-gray-600 rounded-md text-sm bg-gray-800 text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                aria-label="Filter by status"
              >
                <option value="all">All</option>
                <option value="running">Running</option>
                <option value="stopped">Stopped</option>
                <option value="pending">Pending</option>
                <option value="creating">Creating</option>
                <option value="error">Error</option>
              </select>
            </div>
          </div>
        </div>

        {/* VM Table */}
        {isLoading ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin mb-4"></div>
              <p className="text-sm text-gray-400">Loading virtual machines...</p>
            </div>
          </div>
        ) : isError ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-12">
            <div className="text-center">
              <p className="text-red-400 mb-4">Failed to load VMs</p>
              <button
                onClick={() => refetch()}
                className="btn-primary"
              >
                Retry
              </button>
            </div>
          </div>
        ) : vms.length === 0 ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-800 p-12">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-700 mb-4">
                <svg
                  className="h-8 w-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">No Virtual Machines</h3>
              <p className="mt-2 text-sm text-gray-400 max-w-md mx-auto">
                {selectedProject
                  ? "You don't have any VMs in this project yet. Create your first virtual machine to get started."
                  : "Select a project above or create a new virtual machine to get started."}
              </p>
              <div className="mt-8">
                <button
                  onClick={handleCreateVM}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors shadow-sm"
                >
                  <PlusIcon className="w-5 h-5" />
                  Create Virtual Machine
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="relative bg-gray-900 rounded-xl shadow-sm border border-gray-800 overflow-hidden">
              {isFetching && !isLoading && (
                <div className="absolute top-2 right-3 z-10 flex items-center gap-2 rounded-md bg-gray-800/90 px-2 py-1 text-xs text-gray-300">
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-500 border-t-primary-500" />
                  Refreshing…
                </div>
              )}
              <table className="w-full">
                <thead className="bg-gray-800 border-b border-gray-700">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      CPU
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      RAM
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Storage
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Public Access
                    </th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {vms.map((vm) => (
                    <VMTableRow
                      key={vm.id}
                      vm={vm}
                      onStart={handleStartVM}
                      onStop={handleStopVM}
                      onRestart={handleRestartVM}
                      onDelete={handleDeleteVM}
                      onRetry={handleRetryVM}
                      onSyncStatus={handleSyncStatus}
                      isStarting={isStarting}
                      isStopping={isStopping}
                      isRestarting={isRestarting}
                      isDeleting={isDeleting}
                      isRetrying={isRetrying}
                      isSyncingStatus={isSyncingStatus}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-300">
                  Showing page {currentPage} of {pages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-600 rounded-md text-sm font-medium text-gray-300 bg-gray-900 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    disabled={page >= pages}
                    className="px-4 py-2 border border-gray-600 rounded-md text-sm font-medium text-gray-300 bg-gray-900 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Connection Info Modal */}
      {showConnectionInfo && selectedVM && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white">
                  Connection Info - {selectedVM.name}
                </h2>
                <button
                  onClick={() => {
                    setShowConnectionInfo(false);
                    setSelectedVM(null);
                  }}
                  className="text-gray-600 hover:text-gray-400"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <VMConnectionInfo vm={selectedVM} />
            </div>
          </div>
        </div>
      )}

      {/* Create Snapshot Modal */}
      {showSnapshotModal && (
        <CreateSnapshotModal
          isOpen={showSnapshotModal}
          onClose={() => {
            setShowSnapshotModal(false);
            setSelectedVMForSnapshot(null);
          }}
          onSuccess={() => {
            setShowSnapshotModal(false);
            setSelectedVMForSnapshot(null);
            toast.success('Snapshot creation initiated');
          }}
          initialVMId={selectedVMForSnapshot || undefined}
        />
      )}

      {/* KYC Required Modal */}
      <KYCRequiredModal
        isOpen={showKYCModal}
        onClose={() => setShowKYCModal(false)}
      />
    </div>
  );
};

export default VMs;